import React from 'react';
import useModal from '@/hooks/useModal';
import { CaseClientList } from '../CaseClientList';
import { Button, Icon, Typography } from '@/components/common';
import { CaseClientTitle } from '../CaseClientTitle';
import { CaseClientsCounter, PhaseName } from '@/interfaces';
import { CaseClientFilters } from '../filters';
import { InputCheck } from '@/components/form';
import Icons from '@/const/icons';

interface Props {
	phaseName?: PhaseName;
	open: boolean;
	setOpen: (open: boolean) => void;
	selectedUserIds: (string | null)[];
	setSelectedUserIds: React.Dispatch<React.SetStateAction<(string | null)[]>>;
	onContinue: () => void;
	title?: string;
	onClose?: () => void;
}

export const ModalSelectUsers: React.FC<Props> = ({
	phaseName,
	open,
	setOpen,
	selectedUserIds,
	setSelectedUserIds,
	onContinue,
	title = 'Request Document',
	onClose,
}) => {
	const { Modal, show, hide } = useModal();
	const [totalItems, setTotalItems] = React.useState<CaseClientsCounter>({
		all: 0,
		total: 0,
		complete: 0,
		pending: 0,
		review: 0,
		rejected: 0,
	});
	const [currentVisibleUserIds, setCurrentVisibleUserIds] = React.useState<
		string[]
	>([]);

	React.useEffect(() => {
		if (open) {
			show();
		} else {
			hide();
		}
	}, [open, show, hide]);

	const handleClose = React.useCallback(() => {
		hide();
		setOpen(false);
		onClose?.();
	}, [hide, onClose, setOpen]);

	const handleSelectUser = React.useCallback(
		(userId: string | null, checked: boolean) => {
			setSelectedUserIds((prev) => {
				// Handle "Select All" checkbox
				if (userId === null) {
					return checked ? [null] : [];
				}

				// If "Select All" is currently active
				if (prev.includes(null)) {
					if (!checked) {
						// Switching from 'All' to a specific list of visible users EXCEPT the one unchecked
						return currentVisibleUserIds.filter((id) => id !== userId);
					}
					// If already 'All' and checking another one, no change needed
					return prev;
				}

				// Finite selection logic
				if (checked) {
					return prev.includes(userId) ? prev : [...prev, userId];
				} else {
					return prev.filter((id) => id !== userId);
				}
			});
		},
		[currentVisibleUserIds, setSelectedUserIds]
	);

	const totalSelected = React.useMemo(() => {
		if (selectedUserIds.includes(null)) {
			return currentVisibleUserIds.length;
		}
		return selectedUserIds.length;
	}, [currentVisibleUserIds, selectedUserIds]);

	return (
		<Modal
			onClose={handleClose}
			title={title}
			classNameTitle="text-left"
			backgroundColor="white"
			withXButton
			classNameCardContainer="lg:w-[780px] w-full"
		>
			<div className="flex flex-col h-full min-h-[60vh] w-full">
				<div className="mb-3">
					<CaseClientTitle name={phaseName || 'Investigation'} />
				</div>
				<div className="flex items-center">
					<InputCheck
						name="all"
						checked={selectedUserIds.includes(null as any)}
						onChange={(e) => {
							handleSelectUser(null, e.target.checked);
						}}
						register={(() => ({})) as any}
						className="mb-4"
					/>
					<CaseClientFilters />
				</div>
				<div className="overflow-y-auto max-h-[60vh] scroll-custom">
					<CaseClientList
						showCheckbox
						selectedUserIds={selectedUserIds}
						onSelectUser={handleSelectUser}
						setTotalItems={setTotalItems}
						setCurrentUserIds={setCurrentVisibleUserIds}
					/>
				</div>
				<div className="flex justify-between items-center mt-auto">
					<div className="flex items-center gap-2">
						{totalSelected === 0 && (
							<Icon
								src={Icons.infoCircle}
								width={24}
								height={24}
								className="text-[#292D32]"
							/>
						)}
						<Typography type="regular-12" className="text-opacity-black">
							{totalSelected} of {totalItems.total} users selected
						</Typography>
					</div>
					<div className="flex gap-6">
						<Button
							label="Cancel"
							decoration="line-primary"
							size="fit"
							onClick={handleClose}
						/>
						<Button
							label="Continue"
							decoration="primary"
							size="fit"
							disabled={selectedUserIds.length === 0}
							onClick={onContinue}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};
